import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { EMPTY, from, Observable, of, Subject, switchMap, take, throwError, timeout } from 'rxjs';
import { JwtAuthService } from '../services/auth/jwt-auth.service';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { CryptoDecryptService } from '../services/crypto-decrypt.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject = new Subject<any>();

  constructor(
    private cryptoDecryptService: CryptoDecryptService,
    private jwtAuth: JwtAuthService) {
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(this.addTokenHeader(req)).pipe(
      switchMap((event: HttpResponse<any>) => {
        return this.fixBodyResponse(event);
      }),
      catchError((error: HttpErrorResponse) => {
        if (req.url.includes('token')) {
          if (this.refreshTokenInProgress) {
            this.jwtAuth.logout(true);
            return EMPTY;
          }
          return this.formatError(error);
        }

        if (error && error.status === 401) {
          if (this.refreshTokenInProgress) {
            // Se já estiver em refresh, espera até concluir
            return this.refreshTokenSubject.pipe(
              filter(result => result !== null),
              take(1),
              switchMap(() => {
                this.refreshTokenSubject.next(true);
                return next.handle(this.addTokenHeader(req)).pipe(
                  switchMap((event: HttpResponse<any>) => {
                    return this.fixBodyResponse(event);
                  }),
                );
              }),
            );
          } else {
            if (this.jwtAuth.getJwtRefreshToken()) {
              this.refreshTokenInProgress = true;
              return this.jwtAuth.refreshToken().pipe(
                switchMap(() => {
                  this.refreshTokenSubject.next(true);
                  return next.handle(this.addTokenHeader(req)).pipe(
                    switchMap((event: HttpResponse<any>) => {
                      return this.fixBodyResponse(event);
                    }),
                  );
                }),
              );
            } else {
              this.jwtAuth.logout(true);
            }
          }
        }
        return this.formatError(error);
      }),
    );
  }

  fixBodyResponse(event: HttpResponse<any>) {
    if (
      event instanceof HttpResponse
      && ['rbe1c2', 'rbe1c1'].includes(event.headers.get('content-encoding'))
    ) {
      const encoding: any = event.headers.get('content-encoding');
      const token = this.jwtAuth.token || this.jwtAuth.getJwtToken();
      return from(
        this.cryptoDecryptService.decryptBody(event.body, token, encoding)
      ).pipe(
        map(decryptedBody => {
          return event.clone({
            body: decryptedBody,
          });
        })
      );
    } else if (event instanceof HttpResponse) {
      let parsedBody: any;
      try {
        if (event.body instanceof ArrayBuffer) {
          // Se for ArrayBuffer → converte para string e tenta JSON
          const text = new TextDecoder().decode(event.body);
          parsedBody = JSON.parse(text);
        } else if (typeof event.body === 'string') {
          // Se for string → tenta JSON.parse
          parsedBody = JSON.parse(event.body);
        } else {
          // Se já for objeto/json ou outro tipo → usa direto
          parsedBody = event.body;
        }
      } catch (e) {
        console.warn('⚠️ Falha ao parsear body, retornando cru:', e);
        parsedBody = event.body;
      }

      return [event.clone({ body: parsedBody })];
    }

    return [event];
  }

  formatError(error: HttpErrorResponse) {
    if (!error?.error) {
      return throwError(() => error);
    }

    const encoding: any = error.headers?.get?.('content-encoding');

    // Corpo de erro criptografado: descriptografa igual ao caminho de sucesso
    // (fixBodyResponse). Sem isso, o JSON.parse cru quebra sobre bytes binários.
    if (['rbe1c2', 'rbe1c1'].includes(encoding) && error.error instanceof ArrayBuffer) {
      const token = this.jwtAuth.token || this.jwtAuth.getJwtToken();
      return this.cryptoDecryptService.decryptBody(error.error, token, encoding).pipe(
        map(decryptedBody => new HttpErrorResponse({ ...error, error: decryptedBody })),
        // Se a descriptografia falhar, cai no parse cru como último recurso
        catchError(() => of(new HttpErrorResponse({ ...error, error: this.parseErrorBody(error.error) }))),
        switchMap(formatted => throwError(() => formatted))
      );
    }

    return throwError(() => new HttpErrorResponse({ ...error, error: this.parseErrorBody(error.error) }));
  }

  private parseErrorBody(body: any): any {
    try {
      if (body instanceof ArrayBuffer) {
        // Decodifica ArrayBuffer -> string e tenta JSON
        const decoded = new TextDecoder().decode(body);
        try {
          return JSON.parse(decoded);
        } catch {
          return decoded; // Se não for JSON, devolve como string
        }
      }
      if (typeof body === 'string') {
        // Se já for string, tenta parsear como JSON
        try {
          return JSON.parse(body);
        } catch {
          return body; // Se não for JSON, mantém a string
        }
      }
      // Já é objeto ou outro tipo (ex.: JSON já pronto)
      return body;
    } catch (e) {
      console.warn('⚠️ Falha ao processar erro:', e);
      return body;
    }
  }

  addTokenHeader(req: HttpRequest<any>) {
    const token = this.jwtAuth.getJwtToken();
    const excludeUrlS3 = '.s3.us-east-2.amazonaws.com';
    const excludeToken = '/v1/token';
    if (
      token &&
      req.url.indexOf(excludeUrlS3) === -1 &&
      req.url.indexOf(excludeToken) === -1
    ) {
      return req.clone({
        setHeaders: {
          Authorization: `Bearer ${ token }`,
        },
        responseType: 'arraybuffer' as 'json'
      });
    }
    return req;
  }
}
